import React, { useContext, useEffect, useState } from 'react';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import { makeStyles } from '@material-ui/core';
import { empty, req_stock_item_chain_code } from '../../Helper/Constants';
import { de } from '../../Helper/Helper';
import API_Service, { axiosDefaultConfig, axiosPutConfig, axiosURIencode } from '../../Services/API_Service';
import AppContext from '../../Services/AppContext';
import { useSnackbar } from 'notistack';

const useStyles = makeStyles((theme) => ({
	dialogTitle: {
		paddingBottom: theme.spacing(0.5),
	},
	dialogContent: {
		paddingTop: theme.spacing(1),
	},
	textField: {
		marginBottom: theme.spacing(2.5),
	},
	formControl: {
		marginBottom: theme.spacing(2.5),
	},
	chips: {
		display: 'flex',
		flexWrap: 'wrap',
	},
	chip: {
		margin: theme.spacing(0.25),
		fontWeight: theme.typography.fontWeightMedium,
	},
	dialogActions: {
		padding: theme.spacing(1, 3, 2),
	},
	saveButton: {
		minWidth: 100,
		fontWeight: theme.typography.fontWeightMedium,
	},
}));

export default function AddEditItemChainCode({ open, onClose, language, data, product_code, uomList, chainList, onUpdate }) {
	const classes = useStyles();
	const [gState] = useContext(AppContext);
	const { enqueueSnackbar } = useSnackbar();
	const [itemChainCode, setItemChainCode] = useState(empty);
	const [selectedChain, setSelectedChain] = useState(empty);
	const [selectedUoms, setSelectedUoms] = useState([]);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (open) {
			if (data !== empty && data !== null && data !== undefined) {
				setItemChainCode(data.item_chain_code || empty);
				setSelectedChain(data.chain_id || empty);
				const uomStr = data.uom || '';
				setSelectedUoms(uomStr ? uomStr.split(',') : []);
			} else {
				setItemChainCode(empty);
				setSelectedChain(empty);
				setSelectedUoms([]);
			}
		}
	}, [open, data]);

	const snack = (msg, type = 'success') => {
		enqueueSnackbar(msg, {
			variant: type,
			preventDuplicate: true,
			autoHideDuration: type === 'success' ? 2000 : 3000
		});
	};

	const closeDialog = () => {
		setItemChainCode(empty);
		setSelectedChain(empty);
		setSelectedUoms([]);
		onClose();
	};

	const save = () => {
		if (itemChainCode === empty || !itemChainCode) {
			snack(language.item_chain_code + ' ' + (language.field_required || 'is required'), 'warning');
			return;
		}
		if (selectedChain === empty || !selectedChain) {
			snack(language.select_chain + ' ' + (language.field_required || 'is required'), 'warning');
			return;
		}
		if (selectedUoms.length === 0) {
			snack(language.select_uom + ' ' + (language.field_required || 'is required'), 'warning');
			return;
		}

		const uomStr = selectedUoms.join(',');
		setSaving(true);

		if (data !== empty && data !== null && data !== undefined && data.id) {
			// Edit mode - PUT
			const params = axiosURIencode({
				id: data.id,
				item_chain_code: itemChainCode,
				uom: uomStr,
				chain_id: selectedChain
			});
			API_Service.put(req_stock_item_chain_code, params, axiosPutConfig(gState.client))
				.then(({ data }) => {
					setSaving(false);
					const { result, message } = data;
					if (parseInt(result) === 1) {
						snack(message);
						closeDialog();
						onUpdate();
					} else {
						snack(message, 'error');
					}
				})
				.catch(err => { setSaving(false); de(err.message); snack(err.message || 'Network error', 'error'); });
		} else {
			// Add mode - POST
			const formdata = new FormData();
			formdata.append('product_code', product_code);
			formdata.append('item_chain_code', itemChainCode);
			formdata.append('uom', uomStr);
			formdata.append('chain_id', selectedChain);
			API_Service.post(req_stock_item_chain_code, formdata, axiosDefaultConfig(gState.client))
				.then(({ data }) => {
					setSaving(false);
					const { result, message } = data;
					if (parseInt(result) === 1) {
						snack(message);
						closeDialog();
						onUpdate();
					} else {
						snack(message, 'error');
					}
				})
				.catch(err => { setSaving(false); de(err.message); snack(err.message || 'Network error', 'error'); });
		}
	};

	const isEdit = data !== empty && data !== null && data !== undefined && data.id;

	return (
		<Dialog onClose={closeDialog} open={open} maxWidth="sm" fullWidth>
			<DialogTitle className={classes.dialogTitle}>
				{isEdit ? language.edit_item_chain_code : language.new_item_chain_code}
			</DialogTitle>
			<DialogContent className={classes.dialogContent}>
				<TextField
					className={classes.textField}
					fullWidth
					variant="outlined"
					id="input-item-chain-code"
					label={language.item_chain_code}
					value={itemChainCode === empty ? '' : itemChainCode}
					placeholder={language.item_chain_code}
					onChange={(e) => setItemChainCode(e.target.value)}
				/>
				<FormControl className={classes.formControl} fullWidth variant="outlined">
					<InputLabel>{language.select_chain}</InputLabel>
					<Select
						value={selectedChain === empty ? '' : selectedChain}
						onChange={(e) => setSelectedChain(e.target.value)}
						label={language.select_chain}
					>
						{chainList && chainList.map((chain, index) => (
							<MenuItem key={String(index)} value={chain.cust_chain_id}>
								{chain.cust_chain_name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<FormControl className={classes.formControl} fullWidth variant="outlined">
					<InputLabel>{language.select_uom}</InputLabel>
					<Select
						multiple
						value={selectedUoms}
						onChange={(e) => setSelectedUoms(e.target.value)}
						label={language.select_uom}
						renderValue={(selected) => (
							<div className={classes.chips}>
								{selected.map((value) => (
									<Chip key={value} label={value} className={classes.chip} size="small" color="primary" />
								))}
							</div>
						)}
					>
						{uomList && uomList.map((uom, index) => (
							<MenuItem key={String(index)} value={uom.product_uom}>
								<Checkbox color="primary" checked={selectedUoms.indexOf(uom.product_uom) > -1} />
								<ListItemText primary={uom.product_uom} />
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</DialogContent>
			<DialogActions className={classes.dialogActions}>
				<Button onClick={closeDialog} disabled={saving}>
					{language.cancel}
				</Button>
				<Button
					onClick={save}
					variant="contained"
					color="primary"
					disabled={saving}
					className={classes.saveButton}
				>
					{saving ? <CircularProgress size={20} /> : language.save}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
